import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { toast } from 'sonner';
import { Capacitor } from '@capacitor/core';
import { Geolocation } from '@capacitor/geolocation';

interface Location {
  latitude: number;
  longitude: number;
}

interface ManualLocation {
  latitude: number;
  longitude: number;
  address: string;
}

interface LocationSettings {
  radius: number;
  unit: 'km' | 'mi';
  manualLocation: ManualLocation | null;
  defaultMode: 'eat-out' | 'eat-in';
}

interface LocationContextType {
  userLocation: Location | null;
  defaultLocation: Location;
  locationName: string;
  isLoadingLocation: boolean;
  requestLocation: () => void;
  settings: LocationSettings;
  updateSettings: (settings: Partial<LocationSettings>) => void;
  activeLocation: Location;
  activeLocationName: string;
  getRadiusInKm: () => number;
}

const LocationContext = createContext<LocationContextType | undefined>(undefined);

// Default location: Camden Market, London
const DEFAULT_LOCATION: Location = {
  latitude: 51.5414,
  longitude: -0.1471
};

const reverseGeocode = async (lat: number, lng: number): Promise<string> => {
  try {
    const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
    const response = await fetch(
      `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&result_type=locality|sublocality&key=${apiKey}`
    );
    if (!response.ok) throw new Error('Geocoding failed');
    const data = await response.json();
    if (data.status !== 'OK' || !data.results?.length) return 'your area';
    const pick = (types: string[]) =>
      data.results[0].address_components.find((c: any) => types.some((t: string) => c.types.includes(t)))?.long_name;
    return (
      pick(['locality']) ||
      pick(['sublocality', 'sublocality_level_1']) ||
      pick(['administrative_area_level_2']) ||
      pick(['administrative_area_level_1']) ||
      'your area'
    );
  } catch (error) {
    console.error('Reverse geocoding error:', error);
    return 'your area';
  }
};

const DEFAULT_SETTINGS: LocationSettings = {
  radius: 5,
  unit: 'km',
  manualLocation: null,
  defaultMode: 'eat-out',
};

export const LocationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [userLocation, setUserLocation] = useState<Location | null>(null);
  const [locationName, setLocationName] = useState<string>('Camden Market');
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);
  const [settings, setSettings] = useState<LocationSettings>(() => {
    const cached = localStorage.getItem('locationSettings');
    if (cached) {
      try {
        return JSON.parse(cached);
      } catch (error) {
        console.error('Error parsing cached settings:', error);
      }
    }
    return DEFAULT_SETTINGS;
  });

  const requestLocation = React.useCallback(async () => {
    setIsLoadingLocation(true);

    try {
      if (Capacitor.isNativePlatform()) {
        // Native (iOS/Android): OS dialog fires here; bail if denied
        const permission = await Geolocation.requestPermissions();
        if (permission.location !== 'granted' && permission.coarseLocation !== 'granted') {
          toast.error('Location access denied. Please enable it in your device settings.');
          setLocationName('Camden Market');
          setUserLocation(null);
          return;
        }
      }
      // Web: skip requestPermissions() — browser prompt fires inside getCurrentPosition()
      // Native: permission already confirmed above

      const position = await Geolocation.getCurrentPosition({
        enableHighAccuracy: true,
        timeout: 10000,
      });

      const newLocation: Location = {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude
      };

      const name = await reverseGeocode(newLocation.latitude, newLocation.longitude);
      setUserLocation(newLocation);
      setLocationName(name);
      localStorage.setItem('userLocation', JSON.stringify(newLocation));
      localStorage.setItem('locationName', name);

      console.log(`Location updated: ${name}`);

    } catch (error: unknown) {
      // GeolocationPositionError code 1 = PERMISSION_DENIED (thrown by browser on web)
      const isPermissionDenied =
        (error as GeolocationPositionError)?.code === 1 ||
        (error as { message?: string })?.message?.toLowerCase().includes('denied');

      if (isPermissionDenied) {
        toast.error('Location access denied. Please enable it in your browser settings.');
      } else {
        console.error('Error getting location:', error);
        toast.error('Unable to get your location.');
      }
      setLocationName('Camden Market');
      setUserLocation(null);
    } finally {
      setIsLoadingLocation(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // On mount: show cached location immediately, then always fetch fresh GPS
  useEffect(() => {
    const cachedLocation = localStorage.getItem('userLocation');
    const cachedLocationName = localStorage.getItem('locationName');

    if (cachedLocation && cachedLocationName) {
      try {
        const location = JSON.parse(cachedLocation);
        setUserLocation(location);
        setLocationName(cachedLocationName);
      } catch (error) {
        console.error('Error parsing cached location:', error);
      }
    }

    requestLocation();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const updateSettings = React.useCallback((newSettings: Partial<LocationSettings>) => {
    setSettings((prev) => {
      const updated = { ...prev, ...newSettings };
      localStorage.setItem('locationSettings', JSON.stringify(updated));
      return updated;
    });
  }, []);

  const activeLocation = useMemo((): Location => {
    if (settings.manualLocation) {
      return {
        latitude: settings.manualLocation.latitude,
        longitude: settings.manualLocation.longitude,
      };
    }
    return userLocation || DEFAULT_LOCATION;
  }, [settings.manualLocation, userLocation]);

  const activeLocationName = useMemo((): string => {
    if (settings.manualLocation) {
      return settings.manualLocation.address;
    }
    return userLocation ? locationName : 'Camden Market';
  }, [settings.manualLocation, userLocation, locationName]);

  const getRadiusInKm = React.useCallback((): number => {
    return settings.unit === 'km' ? settings.radius : settings.radius * 1.60934;
  }, [settings.unit, settings.radius]);

  const value = useMemo<LocationContextType>(() => ({
    userLocation,
    defaultLocation: DEFAULT_LOCATION,
    locationName,
    isLoadingLocation,
    requestLocation,
    settings,
    updateSettings,
    activeLocation,
    activeLocationName,
    getRadiusInKm,
  }), [
    userLocation,
    locationName,
    isLoadingLocation,
    requestLocation,
    settings,
    updateSettings,
    activeLocation,
    activeLocationName,
    getRadiusInKm,
  ]);

  return (
    <LocationContext.Provider value={value}>
      {children}
    </LocationContext.Provider>
  );
};

export const useLocation = (): LocationContextType => {
  const context = useContext(LocationContext);
  if (context === undefined) {
    throw new Error('useLocation must be used within a LocationProvider');
  }
  return context;
};
